import { useEffect, useState } from "react";
import type { DragEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { BoardCard, BoardColumn, BoardView, StrategySummary } from "@/lib/types";
import { Card } from "@/components/Card";
import PhaseMoveDialog from "@/components/PhaseMoveDialog";
import StatusBadge from "@/components/StatusBadge";
import { FlowStateBadge } from "@/components/EmailStatusBadge";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * Kanban view of one strategy's prospect journey. The columns are the
 * strategy's own phases, in the order its document declares them — the board
 * renders whatever journey a strategy configures and knows nothing about any
 * particular one. Prospects that have not entered a phase yet get a synthetic
 * "Not started" column ahead of the journey, shown only when it is non-empty.
 *
 * <p>Cards can be dragged between columns to override the phase the agent has
 * a prospect in — the human half of the state machine, for everything that
 * moves a deal without arriving as an email. The drop opens
 * {@link PhaseMoveDialog} rather than applying immediately, because the note it
 * collects is what makes the move useful to the agent afterwards.
 *
 * <p>The synthetic column is a drag source but not a drop target: un-starting a
 * prospect is not a phase transition, and the audit row that records every move
 * requires a real destination phase.
 */
export default function PipelinePage() {
  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  const [strategySlug, setStrategySlug] = useState<string | null>(null);
  useEffect(() => {
    if (strategySlug) return;
    const first = strategiesQ.data?.[0];
    if (first) setStrategySlug(first.slug);
  }, [strategiesQ.data, strategySlug]);

  const boardQ = useQuery({
    queryKey: ["board", strategySlug],
    enabled: !!strategySlug,
    queryFn: () =>
      api.get<BoardView>(`/api/v1/strategies/${strategySlug}/board?cardsPerColumn=50`),
    // A board is for watching: operators leave it open while the agent works.
    refetchInterval: 30_000,
  });

  const board = boardQ.data;
  const boardEmpty =
    !!board &&
    board.unassigned.totalCount === 0 &&
    board.columns.every((c) => c.totalCount === 0);

  /** The move an operator has started but not yet confirmed. */
  const [move, setMove] = useState<{
    card: BoardCard;
    from: BoardColumn;
    toPhaseId: string | null;
  } | null>(null);

  // Which card is in flight, so every column knows whether to offer itself as a
  // drop target. dataTransfer cannot be read during dragover, so the identity
  // of the dragged card has to live here.
  const [dragging, setDragging] = useState<{ id: string; fromPhaseId: string | null } | null>(null);

  const columnOf = (phaseId: string | null): BoardColumn | null => {
    if (!board) return null;
    if (phaseId === null) return board.unassigned;
    return board.columns.find((c) => c.phaseId === phaseId) ?? null;
  };

  const startMove = (card: BoardCard, fromPhaseId: string | null, toPhaseId: string | null) => {
    const from = columnOf(fromPhaseId);
    if (from) setMove({ card, from, toPhaseId });
  };

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pipeline</h1>
          <p className="text-sm text-[var(--fg-muted)]">
            Every prospect, by the phase the strategy currently has them in. Drag a card to
            another column to move them by hand.
          </p>
        </div>
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Strategy</div>
          <select
            value={strategySlug ?? ""}
            onChange={(e) => setStrategySlug(e.target.value || null)}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white min-w-[16rem]"
            disabled={strategiesQ.isLoading || !strategiesQ.data?.length}
          >
            {strategiesQ.data?.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.name} ({s.slug})
              </option>
            ))}
            {!strategiesQ.data?.length ? <option value="">No strategies</option> : null}
          </select>
        </label>
      </header>

      {strategiesQ.data && strategiesQ.data.length === 0 ? (
        <Card className="max-w-xl">
          <div className="text-sm text-[var(--fg-muted)]">
            No strategies are configured yet, so there is no journey to draw.
          </div>
        </Card>
      ) : boardQ.isLoading || (!board && !boardQ.error) ? (
        <div className="text-sm text-zinc-500">Loading board…</div>
      ) : boardQ.error ? (
        <div className="text-sm text-[var(--danger)]">Failed to load the board.</div>
      ) : boardEmpty ? (
        <Card className="max-w-xl">
          <div className="text-sm font-medium">No prospects yet</div>
          <p className="mt-1 text-sm text-[var(--fg-muted)]">
            Once prospects exist for this strategy, the board shows each one in
            the phase its journey has reached.
          </p>
          <div className="mt-3 flex gap-4 text-sm">
            <Link to="/prospects?import=1" className="text-[var(--primary)] hover:underline">
              Import a contact list →
            </Link>
            <Link to="/prospects" className="text-[var(--primary)] hover:underline">
              Go to prospects →
            </Link>
          </div>
        </Card>
      ) : board ? (
        <div className="overflow-x-auto pb-2">
          <div className="flex items-start gap-4 w-max">
            {board.unassigned.totalCount > 0 ? (
              <BoardColumnView
                column={board.unassigned}
                label="Not started"
                strategyId={board.strategyId}
                dragging={dragging}
                onDragStart={setDragging}
                onDragEnd={() => setDragging(null)}
                onDropCard={startMove}
                onMoveRequest={startMove}
              />
            ) : null}
            {board.columns.map((col) => (
              <BoardColumnView
                key={col.phaseId ?? col.phaseSlug ?? String(col.orderIndex)}
                column={col}
                label={col.phaseName ?? col.phaseSlug ?? "—"}
                strategyId={board.strategyId}
                dragging={dragging}
                onDragStart={setDragging}
                onDragEnd={() => setDragging(null)}
                onDropCard={startMove}
                onMoveRequest={startMove}
              />
            ))}
          </div>
        </div>
      ) : null}

      {move && board ? (
        <PhaseMoveDialog
          card={move.card}
          fromColumn={move.from}
          columns={board.columns}
          initialToPhaseId={move.toPhaseId}
          strategySlug={board.strategySlug}
          onClose={() => setMove(null)}
        />
      ) : null}
    </div>
  );
}

function BoardColumnView({
  column,
  label,
  strategyId,
  dragging,
  onDragStart,
  onDragEnd,
  onDropCard,
  onMoveRequest,
}: {
  column: BoardColumn;
  label: string;
  strategyId: string;
  dragging: { id: string; fromPhaseId: string | null } | null;
  onDragStart: (d: { id: string; fromPhaseId: string | null }) => void;
  onDragEnd: () => void;
  onDropCard: (card: BoardCard, fromPhaseId: string | null, toPhaseId: string) => void;
  onMoveRequest: (card: BoardCard, fromPhaseId: string | null, toPhaseId: string | null) => void;
}) {
  // Terminal phases stay where the journey declares them but are visually
  // muted: they are the board's archive shelf, not its work in progress.
  const viewAllParams = new URLSearchParams({ strategyId });
  if (column.phaseId) viewAllParams.set("phaseId", column.phaseId);

  const [over, setOver] = useState(false);

  // Only a real phase can receive a card, and never the one the card is already
  // in — dropping where you started is not a move.
  const canAccept =
    !!dragging && !!column.phaseId && dragging.fromPhaseId !== column.phaseId;

  return (
    <section
      aria-label={column.terminal ? `${label} (terminal phase)` : label}
      onDragOver={(e) => {
        if (!canAccept) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOver(true);
      }}
      onDragLeave={(e) => {
        // Crossing onto a card inside this column is not leaving the column.
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setOver(false);
      }}
      onDrop={(e) => {
        setOver(false);
        if (!canAccept || !column.phaseId) return;
        e.preventDefault();
        // The card is in the source column, never this one, so it travels in
        // the drag payload rather than being looked up here.
        const card = readDraggedCard(e);
        if (card) onDropCard(card, dragging?.fromPhaseId ?? null, column.phaseId);
      }}
      className={cn(
        "min-w-[17rem] w-[17rem] shrink-0 rounded-lg border border-[var(--border)] bg-zinc-50/50 transition-colors",
        column.terminal && "opacity-70",
        canAccept && "border-dashed",
        over && canAccept && "border-[var(--primary)] bg-blue-50/60",
      )}
    >
      <header
        className={cn(
          "flex items-center justify-between gap-2 px-3 py-2 border-b border-[var(--border)]",
          column.terminal ? "bg-zinc-100 text-zinc-500" : "bg-[var(--bg-card)]",
        )}
      >
        <span className="truncate text-sm font-medium">{label}</span>
        <span className="inline-flex items-center justify-center rounded-full bg-zinc-200 text-zinc-700 text-[11px] font-semibold min-w-[1.25rem] h-5 px-1.5 tabular-nums">
          {column.totalCount}
        </span>
      </header>
      <div className="p-2 space-y-2">
        {column.cards.length === 0 ? (
          <div className="px-1 py-3 text-center text-xs text-[var(--fg-muted)]">
            {over && canAccept ? "Drop to move here" : "Empty"}
          </div>
        ) : (
          column.cards.map((card) => (
            <ProspectCard
              key={card.id}
              card={card}
              phaseId={column.phaseId}
              phaseName={column.phaseName ?? label}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onMoveRequest={onMoveRequest}
            />
          ))
        )}
        {column.cards.length < column.totalCount ? (
          <Link
            to={`/prospects?${viewAllParams.toString()}`}
            className="block px-1 py-1.5 text-center text-xs text-[var(--primary)] hover:underline"
          >
            View all {column.totalCount} →
          </Link>
        ) : null}
      </div>
    </section>
  );
}

/**
 * The dragged card, serialized into the drag payload on dragstart so the drop
 * target can act on it without searching every other column for it.
 */
function readDraggedCard(e: DragEvent): BoardCard | null {
  try {
    const raw = e.dataTransfer.getData("application/x-dealflow-card");
    return raw ? (JSON.parse(raw) as BoardCard) : null;
  } catch {
    return null;
  }
}

function ProspectCard({
  card,
  phaseId,
  phaseName,
  onDragStart,
  onDragEnd,
  onMoveRequest,
}: {
  card: BoardCard;
  phaseId: string | null;
  phaseName: string;
  onDragStart: (d: { id: string; fromPhaseId: string | null }) => void;
  onDragEnd: () => void;
  onMoveRequest: (card: BoardCard, fromPhaseId: string | null, toPhaseId: string | null) => void;
}) {
  const name = card.fullName ?? card.email;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("application/x-dealflow-prospect", card.id);
        e.dataTransfer.setData("application/x-dealflow-card", JSON.stringify(card));
        onDragStart({ id: card.id, fromPhaseId: phaseId });
      }}
      onDragEnd={onDragEnd}
      className="relative rounded-md border border-[var(--border)] bg-[var(--bg-card)] shadow-sm hover:ring-2 hover:ring-blue-300 cursor-grab active:cursor-grabbing"
    >
      {/* Keyboard and screen-reader path to the same dialog the drop opens.
          Drag-and-drop is the shortcut, never the only way in. */}
      <button
        type="button"
        onClick={() => onMoveRequest(card, phaseId, null)}
        title={`Move ${name} to another phase`}
        aria-label={`Move ${name} to another phase`}
        className="absolute right-1.5 top-1.5 z-10 rounded px-1.5 py-0.5 text-[11px] text-[var(--fg-muted)] hover:bg-zinc-100 hover:text-zinc-700"
      >
        Move
      </button>
      <Link to={`/prospects/${card.id}`} className="block p-3 pr-14">
        <div className="truncate text-sm font-medium">{name}</div>
        {card.fullName ? (
          <div className="truncate text-xs text-[var(--fg-muted)]">{card.email}</div>
        ) : null}
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <StatusBadge status={card.status} />
          <FlowStateBadge state={card.flowState} />
          {card.awaitingApproval ? (
            <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2 py-0.5 text-xs font-medium whitespace-nowrap">
              Awaiting approval
            </span>
          ) : null}
          {card.qualificationTotal > 0 ? (
            <span className="inline-flex items-center rounded-full bg-zinc-100 text-zinc-600 px-2 py-0.5 text-xs whitespace-nowrap tabular-nums">
              {card.qualificationAnswered}/{card.qualificationTotal} answered
            </span>
          ) : null}
        </div>
        {card.latestNote ? (
          // Why this card is in this column. Clamped, because the note's job
          // here is to stop the operator wondering, not to be read in full.
          <div
            className="mt-1.5 rounded bg-zinc-50 px-2 py-1 text-[11px] text-zinc-600 ring-1 ring-zinc-100 line-clamp-2"
            title={card.latestNote}
          >
            <span className="font-medium text-zinc-500">Note: </span>
            {card.latestNote}
          </div>
        ) : null}
        <div className="mt-1.5 text-[11px] text-[var(--fg-muted)]">
          Last activity {formatDateTime(card.lastActivityAt)}
        </div>
      </Link>
      <span className="sr-only">Currently in {phaseName}</span>
    </div>
  );
}
